import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Loader2 } from 'lucide-react';
import SEOHead from '../components/SEOHead';
import AdminHeader from '../components/admin/AdminHeader';
import AdminStatsGrid from '../components/admin/AdminStatsGrid';
import SourceBreakdownGrid from '../components/admin/SourceBreakdownGrid';
import LeadsTable from '../components/admin/LeadsTable';
import LeadsToolbar from '../components/admin/LeadsToolbar';
import useAdminAuth, { API, HTTP_UNAUTHORIZED } from '../hooks/useAdminAuth';
import { devError } from '../lib/devLog';

const EMPTY_DATA = { sellerLeads: [], buyerLeads: [], combined: [] };

// Module-level helpers — pure functions, no need to be inside the component.
const matchesSearch = (lead, needle) => {
  if (!needle) return true;
  const haystack = [
    lead.fullName, lead.email, lead.cityState, lead.companyName, lead.source,
  ].filter(Boolean).join(' ').toLowerCase();
  return haystack.includes(needle.toLowerCase());
};

const pickTab = (tab, data) => {
  if (tab === 'seller') return data.sellerLeads.map((l) => ({ ...l, type: 'seller' }));
  if (tab === 'buyer') return data.buyerLeads.map((l) => ({ ...l, type: 'buyer' }));
  return data.combined;
};

const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const AdminDashboardPage = () => {
  const { checking, authed, logout } = useAdminAuth();
  const [stats, setStats] = useState(null);
  const [data, setData] = useState(EMPTY_DATA);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [tab, setTab] = useState('all');
  const [search, setSearch] = useState('');
  const [sourceFilter, setSourceFilter] = useState('all');

  const loadData = useCallback(async () => {
    setRefreshing(true);
    try {
      const [statsRes, leadsRes] = await Promise.all([
        axios.get(`${API}/leads/stats`),
        axios.get(`${API}/admin/leads`, { withCredentials: true }),
      ]);
      setStats(statsRes.data);
      setData(leadsRes.data);
    } catch (err) {
      devError('admin data load failed:', err.message);
      if (err.response?.status === HTTP_UNAUTHORIZED) logout();
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [logout]);

  useEffect(() => {
    if (authed) loadData();
  }, [authed, loadData]);

  const exportCsv = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/leads/export.csv?lead_type=${tab}`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const date = new Date().toISOString().slice(0, 10);
      downloadBlob(new Blob([res.data]), `dreamhouses-leads-${tab}-${date}.csv`);
    } catch (err) {
      devError('csv export failed:', err.message);
    }
  }, [tab]);

  const sourceOptions = useMemo(() => {
    const set = new Set();
    [...data.sellerLeads, ...data.buyerLeads].forEach((l) => set.add(l.source || 'home'));
    return ['all', ...Array.from(set).sort()];
  }, [data]);

  const rows = useMemo(() => {
    const list = pickTab(tab, data);
    return list.filter((lead) => {
      const sourceOk = sourceFilter === 'all' || (lead.source || 'home') === sourceFilter;
      return sourceOk && matchesSearch(lead, search);
    });
  }, [tab, data, search, sourceFilter]);

  const tabCounts = useMemo(() => ({
    all: data.combined.length,
    seller: data.sellerLeads.length,
    buyer: data.buyerLeads.length,
  }), [data]);

  if (checking || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <SEOHead title="Admin Dashboard — DreamHousesUSA" description="Leads + analytics" canonical="https://www.dreamhousesusa.com/admin/dashboard" />
      <AdminHeader onRefresh={loadData} onLogout={logout} refreshing={refreshing} />
      <main className="container mx-auto px-6 py-8 max-w-7xl">
        <AdminStatsGrid stats={stats} sourcePageCount={sourceOptions.length - 1} />
        <SourceBreakdownGrid stats={stats} />
        <div className="bg-white rounded-xl border border-slate-200">
          <LeadsToolbar
            tab={tab}
            setTab={setTab}
            tabCounts={tabCounts}
            search={search}
            setSearch={setSearch}
            sourceFilter={sourceFilter}
            setSourceFilter={setSourceFilter}
            sourceOptions={sourceOptions}
            onExport={exportCsv}
          />
          <LeadsTable rows={rows} />
        </div>
      </main>
    </div>
  );
};

export default AdminDashboardPage;
